/**
 * Конфигурация API для админ-панели
 * Настройки для подключения к бэкенду
 */

/**
 * Базовый URL API бэкенда
 * Использует VITE_BACKEND_URL переменную окружения для подключения к бэкенду
 */
const getApiBaseUrl = (): string => {
  const backendUrl = import.meta.env.VITE_BACKEND_URL;

  if (!backendUrl) {
    throw new Error('VITE_BACKEND_URL is not defined');
  }

  return backendUrl;
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Таймаут для HTTP запросов (в миллисекундах)
 */
export const API_TIMEOUT = 10000;

/**
 * Ключ для хранения JWT токена в localStorage
 */
export const AUTH_TOKEN_KEY = 'admin_auth_token';

/**
 * Endpoints API
 */
export const API_ENDPOINTS = {
  // Аутентификация
  AUTH: {
    LOGIN: '/admin/auth/login',
    LOGOUT: '/admin/auth/logout',
    VERIFY: '/admin/auth/verify',
    PROFILE: '/admin/auth/profile'
  },

  // Панель управления
  DASHBOARD: {
    STATS: '/admin/dashboard/stats'
  },

  // Врачи
  DOCTORS: {
    LIST: '/admin/doctors',
    DETAIL: (id: number) => `/admin/doctors/${id}`,
    CREATE: '/admin/doctors',
    UPDATE: (id: number) => `/admin/doctors/${id}`,
    UPDATE_STATUS: (id: number) => `/admin/doctors/${id}/status`,
    UPDATE_SORT_ORDER: '/admin/doctors/sort-order',
    UPLOAD_IMAGE: (id: number) => `/admin/doctors/${id}/images`,
    SET_ACTIVE_IMAGE: (id: number, imageId: number) =>
      `/admin/doctors/${id}/images/${imageId}/activate`,
    DELETE_IMAGE: (id: number, imageId: number) =>
      `/admin/doctors/${id}/images/${imageId}`
  },

  // Цены
  PRICES: {
    CATEGORIES: {
      LIST: '/admin/prices/categories',
      DETAIL: (id: number) => `/admin/prices/categories/${id}`,
      CREATE: '/admin/prices/categories',
      UPDATE: (id: number) => `/admin/prices/categories/${id}`,
      UPDATE_STATUS: (id: number) => `/admin/prices/categories/${id}/status`
    },
    ITEMS: {
      LIST: '/admin/prices/items',
      DETAIL: (id: number) => `/admin/prices/items/${id}`,
      CREATE: '/admin/prices/items',
      UPDATE: (id: number) => `/admin/prices/items/${id}`,
      UPDATE_STATUS: (id: number) => `/admin/prices/items/${id}/status`,
      UPDATE_SORT_ORDER: '/admin/prices/items/sort-order'
    }
  },

  // Настройки страниц
  INDEX_PAGE_SETTINGS: {
    GET: '/admin/index-page-settings',
    UPDATE: '/admin/index-page-settings',
    UPLOAD_HERO_IMAGE: '/admin/index-page-settings/hero-image',
    DELETE_HERO_IMAGE: '/admin/index-page-settings/hero-image'
  },

  // Лента фотографий клиники на главной
  GALLERY: {
    LIST: '/admin/gallery',
    UPLOAD: '/admin/gallery',
    DELETE: (id: number) => `/admin/gallery/${id}`,
    UPDATE_SORT_ORDER: '/admin/gallery/sort-order'
  },

  // Информация для потребителей
  CUSTOMER_INFORMATION: {
    GET: '/admin/customer-information',
    UPDATE: '/admin/customer-information',
    UPLOAD_IMAGE: '/admin/customer-information/assets/image',
    UPLOAD_DOCUMENT: '/admin/customer-information/assets/document'
  }
} as const;

/**
 * HTTP статус коды
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500
} as const;

/**
 * Заголовки по умолчанию
 */
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json'
} as const;

/**
 * Конфигурация для загрузки файлов
 */
export const FILE_UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  CHUNK_SIZE: 1024 * 1024 // 1MB chunks for large files
} as const;
